/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { getSystemSettings, saveSystemSettings } from "../../utils/commissionConfig";
import { isSandboxApiKey, isProductionApiKey } from "./utils/financialValidation";

export type PaymentEnvironment = "sandbox" | "production";

export interface PaymentEnvironmentConfig {
  environment: PaymentEnvironment;
  baseUrl: string;
  isConfigured: boolean;
  realMoney: boolean;
}

export interface SetEnvironmentOptions {
  userId?: string;
  isSuperAdmin?: boolean;
  confirmationAccepted?: boolean;
  customProductionKey?: string;
  customSandboxKey?: string;
  persist?: boolean;
}

export interface EnvironmentValidationResult {
  valid: boolean;
  environment: PaymentEnvironment;
  message?: string;
  errors: string[];
}

export function maskApiKey(apiKey: string): string {
  if (!apiKey) return "";
  const clean = apiKey.trim();
  if (clean.length <= 10) return "••••••••";
  const prefix = clean.substring(0, 7);
  const suffix = clean.substring(clean.length - 4);
  return `${prefix}...${suffix}`;
}

const ALS_KEY = Symbol.for("atletahub.asyncLocalStorage.paymentEnvironment");
const OVERRIDE_KEY = Symbol.for("atletahub.runtimeOverride.paymentEnvironment");

let asyncLocalStorageInstance: any = (globalThis as any)[ALS_KEY] || null;
if (!asyncLocalStorageInstance && typeof window === "undefined" && typeof process !== "undefined") {
  try {
    const asyncHooks = typeof require === "function" ? require("async_hooks") : null;
    if (asyncHooks && asyncHooks.AsyncLocalStorage) {
      asyncLocalStorageInstance = new asyncHooks.AsyncLocalStorage();
      (globalThis as any)[ALS_KEY] = asyncLocalStorageInstance;
    }
  } catch {}
}

export class PaymentEnvironmentService {
  private static get _runtimeOverride(): PaymentEnvironment | null {
    return (globalThis as any)[OVERRIDE_KEY] || null;
  }

  private static set _runtimeOverride(val: PaymentEnvironment | null) {
    (globalThis as any)[OVERRIDE_KEY] = val;
  }

  /**
   * Executes an asynchronous operation with an isolated environment override.
   * Completely safe against concurrent requests (zero leakage across async flows).
   */
  static runWithEnvironment<T>(env: PaymentEnvironment, fn: () => Promise<T> | T): Promise<T> | T {
    const normEnv: PaymentEnvironment = env === "production" ? "production" : "sandbox";
    if (asyncLocalStorageInstance) {
      return asyncLocalStorageInstance.run(normEnv, fn);
    }
    const prev = this._runtimeOverride;
    this._runtimeOverride = normEnv;
    try {
      const res = fn();
      if (res && typeof (res as any).then === "function") {
        return (res as Promise<T>).finally(() => {
          this._runtimeOverride = prev;
        });
      }
      this._runtimeOverride = prev;
      return res;
    } catch (err) {
      this._runtimeOverride = prev;
      throw err;
    }
  }

  /**
   * Defines or resets in-memory runtime environment override (for backend server runtime / tests).
   */
  static setRuntimeOverride(env: PaymentEnvironment | null) {
    this._runtimeOverride = env;
  }

  /**
   * Resolves active payment environment ("sandbox" | "production")
   * Priority:
   * 1. Explicit argument / override
   * 2. Isolated AsyncLocalStorage contextual environment (per-request safe)
   * 3. In-memory runtime override
   * 4. Process environment variable (PAYMENT_ENVIRONMENT / ASAAS_ENVIRONMENT)
   * 5. Configured settings in SystemSettings (Firestore / localStorage)
   * 6. Default: "sandbox" (safe fallback)
   */
  static getActiveEnvironment(overrideEnv?: string): PaymentEnvironment {
    if (overrideEnv) {
      const rawOverride = overrideEnv.toString().trim().toLowerCase();
      if (rawOverride === "production" || rawOverride === "prod") return "production";
      if (rawOverride === "sandbox") return "sandbox";
    }

    if (asyncLocalStorageInstance) {
      const storeEnv = asyncLocalStorageInstance.getStore();
      if (storeEnv === "production" || storeEnv === "sandbox") {
        return storeEnv;
      }
    }

    if (this._runtimeOverride) {
      return this._runtimeOverride;
    }

    if (typeof process !== "undefined" && process.env) {
      const envVar = (process.env.PAYMENT_ENVIRONMENT || process.env.ASAAS_ENVIRONMENT || process.env.ASAAS_ENV || "").trim().toLowerCase();
      if (envVar) {
        if (envVar === "production" || envVar === "prod") return "production";
        return "sandbox";
      }
    }

    try {
      const settings = getSystemSettings();
      if (settings.asaasEnvironment) {
        return settings.asaasEnvironment === "production" ? "production" : "sandbox";
      }
      if (settings.paymentEnvironmentMode === "production") return "production";
      if (settings.paymentEnvironmentMode === "sandbox") return "sandbox";
    } catch {}

    return "sandbox";
  }

  /**
   * Safely switches the active payment environment with Super Admin verification and credential safety checks.
   */
  static setActiveEnvironment(
    targetEnv: PaymentEnvironment,
    options: SetEnvironmentOptions = {}
  ): { success: boolean; environment: PaymentEnvironment; message: string; error?: string } {
    const normTarget: PaymentEnvironment = targetEnv === "production" ? "production" : "sandbox";

    // Se estiver ativando PRODUÇÃO:
    if (normTarget === "production") {
      if (!options.confirmationAccepted) {
        return {
          success: false,
          environment: this.getActiveEnvironment(),
          message: "Confirmação explícita de ativação de Produção necessária.",
          error: "PROD_CONFIRMATION_REQUIRED",
        };
      }

      // Validar existência e formato das credenciais de produção
      const existingProdKey = options.customProductionKey || this.getApiKey("production");
      const isConfiguredInSettings = Boolean(getSystemSettings().asaasProductionConfigured);
      if (!existingProdKey && !isConfiguredInSettings) {
        return {
          success: false,
          environment: this.getActiveEnvironment(),
          message: "Bloqueio de Segurança: Não é possível ativar o modo Produção sem credenciais de Produção configuradas.",
          error: "PROD_CREDENTIALS_MISSING",
        };
      }

      if (existingProdKey && isSandboxApiKey(existingProdKey)) {
        return {
          success: false,
          environment: this.getActiveEnvironment(),
          message: "Bloqueio de Segurança: Chave de Sandbox detectada no ambiente de Produção. Operação abortada.",
          error: "PROD_HAS_SANDBOX_KEY",
        };
      }
    }

    // Se estiver no Sandbox:
    if (normTarget === "sandbox") {
      const existingSandboxKey = options.customSandboxKey || this.getApiKey("sandbox");
      if (existingSandboxKey && isProductionApiKey(existingSandboxKey)) {
        return {
          success: false,
          environment: this.getActiveEnvironment(),
          message: "Bloqueio de Segurança: Chave de Produção detectada no ambiente Sandbox. Operação abortada.",
          error: "SANDBOX_HAS_PROD_KEY",
        };
      }
    }

    // Aplica o override em tempo de execução
    this._runtimeOverride = normTarget;

    // Se persistência estiver habilitada
    if (options.persist !== false) {
      try {
        const settings = getSystemSettings();
        const updated = {
          ...settings,
          asaasEnvironment: normTarget,
          paymentEnvironmentMode: normTarget,
        };
        saveSystemSettings(updated);
      } catch (err) {
        console.warn("[PaymentEnvironmentService] Aviso ao persistir configurações de ambiente:", err);
      }
    }

    return {
      success: true,
      environment: normTarget,
      message: normTarget === "production"
        ? "Ambiente de PRODUÇÃO ativado com sucesso. Atenção: Operações financeiras movimentarão dinheiro real."
        : "Ambiente SANDBOX ativado com sucesso. Operações em modo de teste e homologação.",
    };
  }

  /**
   * Resolves base URL for Asaas API based on environment.
   */
  static getBaseUrl(env?: PaymentEnvironment): string {
    const activeEnv = env || this.getActiveEnvironment();
    if (activeEnv === "production") {
      return "https://api.asaas.com/v3";
    }
    return "https://api-sandbox.asaas.com/v3";
  }

  /**
   * Checks if active environment is marked as configured in system metadata.
   */
  static isEnvironmentConfigured(env?: PaymentEnvironment): boolean {
    const activeEnv = env || this.getActiveEnvironment();
    try {
      const settings = getSystemSettings();
      if (activeEnv === "production") {
        return Boolean(settings.asaasProductionConfigured || this.getApiKey("production"));
      }
      return Boolean(settings.asaasSandboxConfigured || this.getApiKey("sandbox"));
    } catch {
      return false;
    }
  }

  /**
   * Helper compatível com testes e serviços internos.
   * Chaves reais nunca são expostas ao browser; no client-side retornam estado ou mock mascarado.
   */
  static getApiKey(env?: PaymentEnvironment): string {
    const activeEnv = env || this.getActiveEnvironment();
    if (typeof process !== "undefined" && process.env) {
      if (activeEnv === "production") {
        const prodKey = (process.env.ASAAS_PRODUCTION_API_KEY || process.env.ASAAS_API_KEY_PRODUCTION || "").trim();
        if (prodKey) {
          if (isSandboxApiKey(prodKey)) {
            console.error("[SAFETY FATAL ERROR]: Sandbox API Key configured inside Production environment variable!");
            throw new Error("[SAFETY FATAL ERROR]: Sandbox API Key configured inside Production environment variable!");
          }
          return prodKey;
        }

        const genericKey = (process.env.ASAAS_API_KEY || "").trim();
        if (genericKey && isProductionApiKey(genericKey)) {
          return genericKey;
        }

        try {
          const settings = getSystemSettings();
          const sysProd = (settings.asaasProductionApiKey || (isProductionApiKey(settings.asaasKey) ? settings.asaasKey : "") || "").trim();
          if (sysProd && isProductionApiKey(sysProd)) return sysProd;
        } catch {}

        return "";
      }

      const sandboxKey = (process.env.ASAAS_SANDBOX_API_KEY || process.env.ASAAS_API_KEY_SANDBOX || "").trim();
      if (sandboxKey) {
        if (isProductionApiKey(sandboxKey)) {
          console.error("[SAFETY FATAL ERROR]: Production API Key configured inside Sandbox environment variable!");
          throw new Error("[SAFETY FATAL ERROR]: Production API Key configured inside Sandbox environment variable!");
        }
        return sandboxKey;
      }

      const genericKey = (process.env.ASAAS_API_KEY || "").trim();
      if (genericKey) {
        if (isSandboxApiKey(genericKey)) {
          return genericKey;
        }
        // Se a chave genérica for de produção, apenas não a usa em sandbox (não joga erro falso)
      }

      try {
        const settings = getSystemSettings();
        const sysSandbox = (settings.asaasSandboxApiKey || (isSandboxApiKey(settings.asaasKey) ? settings.asaasKey : "") || "").trim();
        if (sysSandbox && isSandboxApiKey(sysSandbox)) return sysSandbox;
      } catch {}

      return "";
    }
    return "";
  }

  /**
   * Helper compatível com testes e serviços internos para Webhook tokens.
   */
  static getWebhookToken(env?: PaymentEnvironment): string {
    const activeEnv = env || this.getActiveEnvironment();
    if (typeof process !== "undefined" && process.env) {
      if (activeEnv === "production") {
        return (process.env.ASAAS_PRODUCTION_WEBHOOK_TOKEN || process.env.ASAAS_WEBHOOK_TOKEN_PRODUCTION || "").trim();
      }
      return (process.env.ASAAS_SANDBOX_WEBHOOK_TOKEN || process.env.ASAAS_WEBHOOK_TOKEN_SANDBOX || "").trim();
    }
    return "";
  }

  /**
   * Validates webhook token string comparison for active or specified environment.
   */
  static validateWebhookToken(headerToken: string, env?: PaymentEnvironment): boolean {
    const expected = this.getWebhookToken(env);
    if (!expected) return true; // Se nenhum token foi configurado no servidor, aceita em homologação
    if (!headerToken) return false;
    return headerToken.trim() === expected.trim();
  }

  /**
   * Valida a integridade dos ambientes para garantir que não há contaminação cruzada de chaves.
   */
  static validateEnvironmentSafety(): EnvironmentValidationResult {
    const errors: string[] = [];
    const active = this.getActiveEnvironment();

    const sandboxKey = this.getApiKey("sandbox");
    if (sandboxKey && isProductionApiKey(sandboxKey)) {
      errors.push("Chave de Produção detectada no ambiente Sandbox.");
    }

    const prodKey = this.getApiKey("production");
    if (prodKey && isSandboxApiKey(prodKey)) {
      errors.push("Chave de Sandbox detectada no ambiente de Produção.");
    }

    return {
      valid: errors.length === 0,
      environment: active,
      errors,
      message: errors.length === 0
        ? "Segregação de ambientes íntegra. Nenhuma chave cruzada detectada."
        : `Falha na segregação de ambientes: ${errors.join(" | ")}`,
    };
  }

  /**
   * Tags a financial record (booking, payment, payout, subscription) with environment metadata.
   */
  static tagFinancialRecord<T extends Record<string, any>>(record: T, env?: PaymentEnvironment): T & { environment: PaymentEnvironment } {
    const active = env || this.getActiveEnvironment();
    return {
      ...record,
      environment: active,
    };
  }

  /**
   * Gets full environment configuration metadata object (NO API KEYS IN CLIENT).
   */
  static getConfig(overrideEnv?: string): PaymentEnvironmentConfig {
    const environment = this.getActiveEnvironment(overrideEnv);
    const baseUrl = this.getBaseUrl(environment);
    const isConfigured = this.isEnvironmentConfigured(environment);
    const realMoney = environment === "production";

    return {
      environment,
      baseUrl,
      isConfigured,
      realMoney,
    };
  }
}

if (typeof globalThis !== "undefined") {
  (globalThis as any).__PaymentEnvironmentService = PaymentEnvironmentService;
}
